import { z } from "zod"

/**
 * Boundary schemas for the card projections.
 *
 * The point is not to reject data — a prototype that hides half the library is
 * worse than one that renders an imperfect card. The point is to make INT's gaps
 * *attributable*: `reportGaps` names the asset and the missing fields so they can
 * go into FINDINGS.md instead of silently becoming a blank card three components
 * down.
 */
export const libraryCardSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  meta: z.string().min(1).optional(),
  imageUrl: z.string().url().optional(),
  href: z.string().startsWith("/"),
})

export type ValidatedLibraryCard = z.infer<typeof libraryCardSchema>

export interface CardGap {
  readonly id: string
  readonly issues: readonly string[]
}

/** Returns the fields that fell short, or undefined when the card is complete. */
export const inspectCard = (card: unknown): CardGap | undefined => {
  const result = libraryCardSchema.safeParse(card)

  if (result.success) {
    return undefined
  }

  const id = (card as { id?: unknown })?.id
  return {
    id: typeof id === "string" ? id : "unknown",
    issues: result.error.issues.map(issue => `${issue.path.join(".")}: ${issue.message}`),
  }
}

/**
 * Logs incomplete cards in development only. Never throws — a gap in INT is a
 * finding, not a crash.
 */
export const reportGaps = (label: string, cards: readonly unknown[]): readonly CardGap[] => {
  const gaps = cards.map(inspectCard).filter((gap): gap is CardGap => gap !== undefined)

  if (gaps.length > 0 && import.meta.env.DEV) {
    console.warn(`[library:${label}] ${gaps.length} item(s) with incomplete graph data`, gaps)
  }

  return gaps
}
